import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AiFillEdit, AiFillDelete } from "react-icons/ai";
import { Modal, message } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useSortBy,
} from "react-table";
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
} from "react-bootstrap";
import MyContext from "MyContext";

const { confirm } = Modal;

function ListDegree() {
  const { endPointUrl, Token } = useContext(MyContext);
  const [degrees, setDegrees] = useState([]);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editForm, setEditForm] = useState({
    Name: "",
    DegreeID: "",
    photo: null,
  });

  const getAllDegrees = async () => {
    try {
      const { data } = await axios.post(`${endPointUrl}/Admin/getAllDegree`, {
        Token,
      });
      if (data.status) {
        setDegrees(data.data);
      }
    } catch (error) {
      console.error("Fetch Error:", error);
    }
  };

  useEffect(() => {
    getAllDegrees();
  }, []);

  const data = useMemo(
    () => (Array.isArray(degrees) ? degrees : []),
    [degrees]
  );

  const columns = useMemo(
    () => [
      {
        Header: "Degree Logo",
        accessor: "photo",
        Cell: ({ value }) => (
          <img
            src={`${endPointUrl}/images/${value}`}
            alt="Photo"
            style={{ width: "50px", height: "50px" }}
          />
        ),
      },
      {
        Header: "Name",
        accessor: "Name",
      },
      {
        Header: "Actions",
        accessor: "DegreeID",
        Cell: ({ row }) => (
          <div>
            <span
              onClick={() => handleEdit(row.original)}
              style={{ cursor: "pointer", marginRight: "10px" }}
            >
              <AiFillEdit
                style={{ fill: "#07b274", height: "1.4em", width: "1.4em" }}
              />
            </span>
            <span
              onClick={() => handleDelete(row.original.DegreeID)}
              style={{ cursor: "pointer" }}
            >
              <AiFillDelete
                style={{ fill: "red", height: "1.4em", width: "1.4em" }}
              />
            </span>
          </div>
        ),
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    setPageSize,
    setGlobalFilter,
    state,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 5 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { globalFilter, pageIndex, pageSize } = state;

  const handleEdit = (rowData) => {
    setEditForm({
      Name: rowData.Name,
      DegreeID: rowData.DegreeID,
      photo: null,
    });
    setIsEditModalVisible(true);
  };

  const handleDelete = (id) => {
    confirm({
      title: "Are you sure you want to delete this degree?",
      icon: <ExclamationCircleOutlined />,
      content: "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      async onOk() {
        try {
          const { data } = await axios.post(
            `${endPointUrl}/Admin/deleteDegree`,
            {
              DegreeID: id,
              Token,
            }
          );
          if (data.status) {
            message.success("Degree deleted successfully");
            getAllDegrees();
          } else {
            message.error(data.message || "Delete failed");
          }
        } catch (error) {
          console.error(error);
          message.error("Something went wrong while deleting");
        }
      },
      onCancel() {
        message.info("Delete cancelled");
      },
    });
  };

  const handleUpdate = async () => {
    const formData = new FormData();
    formData.append("Token", Token);
    formData.append("DegreeID", editForm.DegreeID);
    formData.append("Name", editForm.Name);
    if (editForm.photo) {
      formData.append("photo", editForm.photo);
    }

    try {
      const { data } = await axios.post(
        `${endPointUrl}/Admin/updateDegree`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (data.status) {
        message.success("Degree updated successfully");
        getAllDegrees();
        setIsEditModalVisible(false);
      } else {
        message.error(data.message || "Update failed");
      }
    } catch (error) {
      console.error(error);
      message.error("Something went wrong while updating");
    }
  };

  return (
    <Container fluid>
      <Row>
        <Col lg="12">
          <Card>
            <Card.Header>
              <Card.Title as="h4">All Degrees</Card.Title>
            </Card.Header>
          </Card>
          <Card className="strpied-tabled-with-hover">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <Form.Control
                type="search"
                placeholder="Search Degree ..."
                style={{ maxWidth: "900px" }}
                value={globalFilter || ""}
                onChange={(e) => setGlobalFilter(e.target.value)}
              />
              <Button
                as={Link}
                to="/admin/Degree-Master"
                className="ms-2"
                variant="primary"
              >
                + Add New Degree
              </Button>
            </Card.Header>

            <Card.Body className="table-full-width table-responsive px-0">
              <Table {...getTableProps()} className="table-hover table-striped">
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps(
                            column.getSortByToggleProps()
                          )}
                        >
                          {column.render("Header")}
                          <span>
                            {column.isSorted
                              ? column.isSortedDesc
                                ? " 🔽"
                                : " 🔼"
                              : ""}
                          </span>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <div className="d-flex justify-content-between align-items-center px-3 py-2">
                <div>
                  Page{" "}
                  <strong>
                    {pageIndex + 1} of {pageOptions.length}
                  </strong>
                </div>
                <div>
                  <Button
                    variant="outline-secondary"
                    onClick={() => previousPage()}
                    disabled={!canPreviousPage}
                  >
                    Previous
                  </Button>{" "}
                  <Button
                    variant="outline-secondary"
                    onClick={() => nextPage()}
                    disabled={!canNextPage}
                  >
                    Next
                  </Button>
                </div>
                <Form.Select
                  style={{ width: "120px" }}
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {[5, 10, 20, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      Show {size}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Edit Modal */}
      <Modal
        title="Edit Degree"
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        onOk={handleUpdate}
        okText="Update"
      >
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              value={editForm.Name}
              onChange={(e) =>
                setEditForm({ ...editForm, Name: e.target.value })
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Photo (Optional)</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={(e) =>
                setEditForm({ ...editForm, photo: e.target.files[0] })
              }
            />
          </Form.Group>
        </Form>
      </Modal>
    </Container>
  );
}

export default ListDegree;
